(function () {
  window.ReigningWet = window.ReigningWet || {};

  if (window.ReigningWet.wwv) return;

  function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isSafeLinkUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    try {
      var parsed = new URL(url, window.location.origin);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }

  // Renders a creator name as a link to their public profile when one is
  // known (Supabase-authenticated submissions with a linked profile),
  // otherwise as plain text — legacy waves have no profile. isFounder adds
  // a star + gold treatment for the one account marked profiles.is_founder
  // — a single hardcoded flag, not a general roles/permissions system.
  function renderCreatorName(name, profileUrl, isFounder) {
    var safeName = escapeHtml(name || 'Anonymous');
    var displayName = isFounder ? '★ ' + safeName : safeName;
    var classAttr = isFounder ? ' class="rw-founder-name"' : '';
    if (profileUrl) {
      return '<a href="' + escapeHtml(profileUrl) + '"' + classAttr + '>' + displayName + '</a>';
    }
    return isFounder ? '<span' + classAttr + '>' + displayName + '</span>' : safeName;
  }

  function mapSupabaseSubmission(row, username, isFounder) {
    return {
      name: row.creator_name,
      project: row.project_title,
      link: row.project_url,
      location: row.location_display,
      latitude: row.latitude,
      longitude: row.longitude,
      category: row.category,
      profileUrl: username && window.ReigningWet.getProfileUrl ? window.ReigningWet.getProfileUrl(username) : null,
      isFounder: !!isFounder,
    };
  }

  // Fetches approved (is_published=true) Supabase submissions and maps them
  // to the {name, project, link, location, latitude, longitude, category,
  // profileUrl} shape both the dock and the WetWorldWav page's legacy
  // renderers already expect. Fails soft to an empty list.
  function fetchApprovedSubmissions() {
    if (!window.ReigningWet.supabase) return Promise.resolve([]);
    var supabase = window.ReigningWet.supabase;

    return supabase
      .from('submissions')
      .select(
        'id, creator_name, project_title, project_url, category, location_display, latitude, longitude, created_at, profile_id'
      )
      .eq('is_published', true)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(function (result) {
        if (result.error) throw result.error;
        var rows = result.data || [];
        var profileIds = rows
          .map(function (r) {
            return r.profile_id;
          })
          .filter(Boolean);

        if (!profileIds.length) {
          return rows.map(function (r) {
            return mapSupabaseSubmission(r, null);
          });
        }

        return supabase
          .from('profiles')
          .select('id, username, is_founder')
          .in('id', profileIds)
          .then(function (profileResult) {
            var profileById = {};
            (profileResult.data || []).forEach(function (p) {
              profileById[p.id] = p;
            });
            return rows.map(function (r) {
              var p = profileById[r.profile_id];
              return mapSupabaseSubmission(r, p ? p.username : null, p ? p.is_founder : false);
            });
          });
      })
      .catch(function (error) {
        if (window.console) console.error('[ReigningWet] Failed to load Supabase submissions:', error);
        return [];
      });
  }

  // New Supabase waves surface first (freshest), followed by the legacy
  // feed in its existing order. A true interleaved chronological sort
  // isn't possible client-side since the legacy Apps Script items carry
  // no timestamp field.
  function mergeWithLegacy(legacyData, supabaseData) {
    var legacy = (legacyData || []).map(function (item) {
      return Object.assign({}, item, { profileUrl: item.profileUrl || null });
    });
    return (supabaseData || []).concat(legacy);
  }

  var STATE_ABBREVIATIONS = {
    California: 'CA',
    'New York': 'NY',
    Texas: 'TX',
    Florida: 'FL',
    Georgia: 'GA',
    Illinois: 'IL',
    Washington: 'WA',
    Nevada: 'NV',
    Arizona: 'AZ',
  };

  function formatLocation(location) {
    if (!location) return 'Somewhere';
    var parts = location.split(',').map(function (p) {
      return p.trim();
    });
    var city = parts[0] || '';
    var state = parts.find(function (p) {
      return STATE_ABBREVIATIONS[p];
    });
    return city && state ? city + ', ' + STATE_ABBREVIATIONS[state] : city || location;
  }

  function haversineDistanceKm(lat1, lon1, lat2, lon2) {
    function toRad(deg) {
      return (deg * Math.PI) / 180;
    }
    var R = 6371;
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Groups waves by actual geographic distance (great-circle, via
  // Haversine), not naive coordinate rounding — rounding lat/lon to N
  // decimal places distorts real-world distance differently depending on
  // latitude and direction, and two people picking the "same city" from
  // the location autocomplete can easily land several km apart at
  // different addresses/neighborhoods. City-name text parsing was tried
  // first but rejected: Nominatim's address text format is inconsistent
  // (varies with how specific the search was — a neighborhood name, a
  // street address, or a bare city can all end up as the first comma
  // segment), so it isn't a reliable grouping key. A default 12km radius
  // catches "same city" spread (e.g. downtown to the edge of a mid-sized
  // city) while staying safely short of genuinely distinct nearby cities
  // (e.g. Oakland/San Francisco are ~13.4km apart core-to-core). Greedy
  // single-pass clustering — fine at this scale (fetches are capped at 50
  // rows).
  function groupByProximity(items, radiusKm) {
    radiusKm = radiusKm || 12;
    var groups = [];

    (items || []).forEach(function (item) {
      var lat = parseFloat(item.latitude);
      var lon = parseFloat(item.longitude);
      if (isNaN(lat) || isNaN(lon)) return;

      var target = null;
      for (var i = 0; i < groups.length; i++) {
        if (haversineDistanceKm(lat, lon, groups[i].lat, groups[i].lon) <= radiusKm) {
          target = groups[i];
          break;
        }
      }
      if (!target) {
        target = { lat: lat, lon: lon, items: [] };
        groups.push(target);
      }
      target.items.push(item);
    });

    return groups;
  }

  // Maps a wave-submission error to a user-facing message. The backend
  // triggers raise exceptions whose messages contain the markers
  // "RATE_LIMIT" (posting too fast) or "DUPLICATE_LINK" (that project URL
  // was already shared) — detected here so both the dock and the page show
  // the same friendly wording instead of a generic error.
  function friendlyWaveError(error) {
    var message = (error && error.message) || '';
    if (/RATE_LIMIT/i.test(message)) {
      return "You're sharing waves quickly — give it a few minutes, then drop the next one 🌊";
    }
    if (/DUPLICATE_LINK/i.test(message)) {
      return 'That link is already in The Current — each wave can only be shared once 🌊';
    }
    return "Couldn't send your wave — please try again.";
  }

  // Inserts one new wave into Supabase. Resolves { data } on success,
  // rejects with the Supabase error on failure — callers own their own
  // UI messaging/state.
  function submitWave(data, user, profile) {
    if (!window.ReigningWet.supabase) return Promise.reject(new Error('Account system unavailable'));

    return window.ReigningWet.supabase
      .from('submissions')
      .insert({
        user_id: user.id,
        profile_id: profile ? profile.id : null,
        creator_name: data.name,
        project_title: data.project,
        project_url: data.link,
        category: data.category || null,
        location_display: data.location,
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
      })
      .select()
      .single()
      .then(function (result) {
        if (result.error) throw result.error;
        return result.data;
      });
  }

  window.ReigningWet.wwv = {
    escapeHtml: escapeHtml,
    isSafeLinkUrl: isSafeLinkUrl,
    renderCreatorName: renderCreatorName,
    formatLocation: formatLocation,
    groupByProximity: groupByProximity,
    friendlyWaveError: friendlyWaveError,
    fetchApprovedSubmissions: fetchApprovedSubmissions,
    mergeWithLegacy: mergeWithLegacy,
    submitWave: submitWave,
  };
})();
